import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { HotelSelect } from "../components/HotelSelect";
import { KpiCards } from "../components/dashboard/KpiCards";
import { AiSummary } from "../components/dashboard/AiSummary";
import { Charts } from "../components/dashboard/Charts";
import { FiltersBar, EMPTY_FILTERS } from "../components/dashboard/FiltersBar";
import { FeedbackTable } from "../components/dashboard/FeedbackTable";
import { FeedbackDetail } from "../components/dashboard/FeedbackDetail";
import { ResolveDialog } from "../components/ResolveDialog";
import { api, errMsg } from "../lib/api";

export default function Dashboard() {
  const [hotel, setHotel] = useState("All");
  const [f, setF] = useState(EMPTY_FILTERS);
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useSearchParams();
  const openId = search.get("open");

  const setFilters = (patch) => setF((p) => ({ ...p, ...patch }));
  useEffect(() => {
    const t = setTimeout(() => setQ(f.q), 300);
    return () => { clearTimeout(t); };
  }, [f.q]);

  const params = { ...f, q, hotel };
  const key = JSON.stringify(params);
  const load = useCallback(async () => {
    const p = JSON.parse(key);
    const [a, b] = await Promise.all([api.get("/analytics", { params: p }), api.get("/feedback", { params: p })]);
    setStats(a.data);
    setItems(b.data);
    setOpen((o) => (o ? b.data.find((x) => x.id === o.id) || o : o));
  }, [key]);

  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  useEffect(() => {
    if (!openId || !items.length) return;
    const it = items.find((x) => x.id === openId);
    if (it) setOpen(it);
    setSearch({}, { replace: true });
  }, [openId, items, setSearch]);

  const pending = items.some((i) => i.ai_status === "pending");
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(() => { load().catch(() => {}); }, 6000);
    return () => { clearInterval(t); };
  }, [pending, load]);

  const [resolving, setResolving] = useState(null);
  const onReopen = async (id) => {
    try {
      await api.patch(`/feedback/${id}`, { status: "Pending" });
      toast.success("Reopened as Pending");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (item) => {
    if (!window.confirm(`Delete feedback from ${item.guest_name} (${item.hotel})? This cannot be undone.`)) return;
    try {
      await api.delete(`/feedback/${item.id}`);
      toast.success("Feedback deleted");
      setOpen((o) => (o?.id === item.id ? null : o));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onReprocess = async (id) => {
    try {
      await api.post(`/feedback/${id}/reprocess`);
      toast.success("AI re-analysis started");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onExport = async (format) => {
    setExporting(true);
    try {
      const r = await api.get("/feedback-export", { params: { ...params, format }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tamanna-feedback-${hotel === "All" ? "all" : hotel.toLowerCase().replace(/\s+/g, "-")}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); } finally { setExporting(false); }
  };

  return (
    <div className="relative z-10 min-h-screen">
      <AppHeader><HotelSelect value={hotel} onChange={setHotel} allowAll testId="dashboard-hotel-filter" /></AppHeader>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="rise">
          <div className="eyebrow">Executive analytics</div>
          <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-emerald-950" data-testid="dashboard-title">
            {hotel === "All" ? "All properties" : hotel}
          </h1>
        </div>
        <AiSummary hotel={hotel} onUpdated={load} total={JSON.stringify(f) === JSON.stringify(EMPTY_FILTERS) ? stats?.kpis.total : null} />
        <FiltersBar f={f} set={setFilters} onExport={onExport} exporting={exporting} />
        {stats ? <KpiCards k={stats.kpis} /> : <div className="h-36 rounded-3xl shimmer" />}
        {stats && <Charts data={stats} />}
        <div className="flex items-end justify-between pt-2">
          <div>
            <h2 className="text-2xl font-extrabold text-emerald-950">Feedback log</h2>
            <p className="text-sm text-slate-500" data-testid="feedback-count">{items.length} entries{pending ? " · AI processing new entries" : ""}</p>
          </div>
        </div>
        <FeedbackTable items={items} onResolve={setResolving} onReopen={onReopen} onOpen={setOpen} onDelete={onDelete} />
      </main>
      <FeedbackDetail item={open} onClose={() => setOpen(null)} onResolve={setResolving} onReopen={onReopen} onReprocess={onReprocess} onDelete={onDelete} />
      <ResolveDialog item={resolving} onClose={() => setResolving(null)} onDone={load} />
    </div>
  );
}
